// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/**
 *
 * <HeaderClickable
 *      iconSrc={icon}
 *      onClick={() => !isMobile && history.push(cartUrl ?? "")}
 *      iconProps={{ type: "miniCart" }}
 *      clickableProps={{ "data-test-selector": "cartLink" }}
 *      titleProps={{
 *          "data-test-selector": totalCountDisplay ? "cartLinkQuantity" : "cartLinkSubTotal",
 *      }}
 *      >
 *          <>
 *              {`${translate("Top Message")}`}
 *              <Typography className="sub-title">{`${translate("Bottom Message")}`}</Typography>
 *          </>
 *      </HeaderClickable>
 */

import { CustomAppState } from "@ces/App/Store/Reducers";
import { fontConfig } from "@ces/XDL/PreStyle/typography";
import { color, getSystemColor, hideElement, mergeWithCss, xClassNames, xScreen } from "@ces/XDL/Utilities";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import Clickable, { ClickablePresentationProps, ClickableProps } from "@insite/mobius/Clickable/Clickable";
import Icon, { IconPresentationProps, IconProps, IconWrapper } from "@insite/mobius/Icon";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, MouseEventHandler, useState } from "react";
import { css } from "styled-components";

interface HeaderClickableProps {
    onClick?: MouseEventHandler<any>;
    iconProps?: IconProps;
    rightIconProps?: IconProps;
    titleProps?: TypographyProps;
    title?: string;
    subTitle?: string;
    cartLines?: number;
    isOpen?: boolean;
    extendedStyles?: HeaderClickableStyle;
}

type Props = HeaderClickableProps & ClickableProps;

export interface HeaderClickableStyle {
    clickable?: ClickablePresentationProps;
    icon?: IconPresentationProps;
    title?: TypographyProps;
    cartItem?: {
        wrapper?: InjectableCss;
        text?: TypographyProps;
    };
    cartLines?: TypographyProps;
    rightIcon?: IconPresentationProps;
}

export const headerClickableStyles: HeaderClickableStyle = {
    clickable: {
        css: css`
            &:hover,
            &.opened {
                ${TypographyStyle}:not(.cartLines) {
                    color: ${color("primary")};
                    cursor: pointer;
                }
                ${IconWrapper} {
                    color: ${color("primary")};
                    cursor: pointer;
                }
            }
            &.mobile-mode {
                display: flex;
                justify-content: center;
            }
            &.mobile-mode:empty {
                display: none;
            }

            &:focus {
                outline: none;
            }
        `,
    },
    title: {
        size: 14,
        color: "text",
        weight: fontConfig.weight.bold,
        css: css`
            letter-spacing: 0;
            line-height: 16px;
            display: block;
            max-width: 150px;
            margin-left: 8px;
            white-space: nowrap;
            &:not(.break-words) {
                overflow: hidden;
                text-overflow: ellipsis;
            }
            .break-words {
                white-space: pre-wrap;
            }

            & > .sub-title {
                display: block;
                white-space: nowrap;
                font-weight: 400;
                line-height: 18px;
                margin-bottom: 0;
                overflow: hidden;
                text-overflow: ellipsis;
                color: ${color("common.ui3")};
                font-size: 14px;
            }

            &:hover {
                cursor: pointer;
                color: ${color("primary")};
            }

            ${xScreen("lte", "md")} {
                ${hideElement}
            }
        `,
    },
    icon: {
        color: "text.link",
        css: css`
            svg {
                width: auto;
            }
            ${xScreen("lte", "md")} {
                width: 20px;
            }
        `,
    },
    rightIcon: {
        color: "text.link",
        css: css`
            margin-left: 8px;
            &,
            svg {
                width: 9px;
                height: 12px;
            }
            ${({ isOpen }: any) => (isOpen ? "rotate: 180deg;" : "")}
        `,
    },
    cartLines: {
        size: 14,
        weight: fontConfig.weight.bold,
        color: "text.link",
        css: css`
            border: 1px solid ${color("text.link")};
            border-radius: 22px;
            line-height: 22px;
            height: 28px;
            min-width: 28px;
            display: flex;
            align-items: center;
            justify-content: center;
            ${({ isOpen }: any) =>
                isOpen &&
                `
                border-color: ${getSystemColor("primary.main")};
                background: ${getSystemColor("primary.main")};
                color: ${getSystemColor("common.ui1")};
            `}
        `,
    },
};

const HeaderClickable: FC<Props> = ({
    iconProps,
    rightIconProps,
    titleProps,
    title,
    subTitle,
    extendedStyles,
    isOpen,
    cartLines = 0,
    ...otherProps
}) => {
    const { isTabletScreen } = useAppSelector(state => ({
        isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
    }));
    const classNames = xClassNames.append(
        [isTabletScreen ? "mobile-mode" : "", isOpen ? "opened" : ""],
        otherProps.className,
    );
    const [styles] = useState(() => mergeWithCss(headerClickableStyles, extendedStyles));

    return (
        <Clickable {...styles.clickable} {...otherProps} className={classNames} aria-label={`${title} ${subTitle}`}>
            {iconProps && cartLines === 0 && (
                <Icon
                    {...styles.icon}
                    {...iconProps}
                    size={iconProps.size || (isTabletScreen ? 20 : 16)}
                    className="menu-icon"
                />
            )}
            {cartLines > 0 && (
                <Typography {...styles.cartLines} {...{ isOpen }} className="cartLines">
                    {cartLines}
                </Typography>
            )}
            <Typography {...styles.title} {...titleProps}>
                {title && <Typography>{title}</Typography>}
                {subTitle && <Typography className="sub-title">{subTitle}</Typography>}
            </Typography>
            {rightIconProps && (
                <Icon {...styles.rightIcon} {...rightIconProps} className="menu-right-icon" {...{ isOpen }} />
            )}
        </Clickable>
    );
};

export default HeaderClickable;
